import { useEffect, useState } from 'react';
import { useFindByQuery } from '../../graphql';

/** A tag as `findTags` returns it here; `linkData` is the hydrated asset, null when the caller cannot read it. */
export interface TagLookupTag {
  id: string;
  tagCode: string;
  link?: {
    linkData?: { id: string; workspaces: { id: string }[] } | null;
    type?: string | null;
  } | null;
}

/**
 * The live lookup for the code asked about now, never an earlier one: `tag`
 * keeps the last answer across a code change, which cannot tell "not found"
 * from "failed" from "still waiting".
 */
export type TagLookup =
  | { status: 'pending' }
  | { status: 'error' }
  | { status: 'success'; tag: TagLookupTag | null };

export const useTagByTagCode = (tagCode: string) => {
  const [tag, setTag] = useState<any>(undefined);
  const variables = {
    tagCode: tagCode,
  };

  const { data, isLoading, isError } = useFindByQuery({
    modelName: 'tag',
    query: FIND_TAG_BY_TAGCODE,
    variables: variables,
    options: {
      enabled: !!tagCode,
      staleTime: 0,
      gcTime: 0,
    },
  });

  useEffect(() => {
    if (!data) return;

    setTag(data.items[0]);
  }, [data]);

  const lookup: TagLookup = data
    ? { status: 'success', tag: (data.items[0] as TagLookupTag) ?? null }
    : isError
    ? { status: 'error' }
    : { status: 'pending' };

  return {
    tag,
    isLoading,
    // `linkData` is hydrated into the linked asset and only resolves assets the
    // caller can read, so a tag claimed in another workspace arrives with a null
    // `linkData`. `type` comes straight off the tag doc.
    isInUse: !!tag?.link?.type,
    lookup,
  };
};

const FIND_TAG_BY_TAGCODE = `
  query FindTags($tagCode: String) {
    findTags(filter: {
      tagCode: { eq: $tagCode}
    }) {
      items {
        id
        tagCode
        link {
          linkData
          type
        }
      }
    }
  }
`;
